/**
 * Input Sanitization Utilities
 * Provides comprehensive sanitization for user inputs to prevent XSS attacks
 */

import DOMPurify from 'dompurify';

/**
 * Sanitizes HTML content to prevent XSS attacks
 * Allows safe HTML tags but removes dangerous attributes and scripts
 */
export const sanitizeHTML = (dirty: string): string => {
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: ['b', 'i', 'u', 'em', 'strong', 'br', 'p', 'span'],
    ALLOWED_ATTR: [],
    ALLOW_DATA_ATTR: false,
  });
};

/**
 * Sanitizes plain text input by escaping HTML entities
 * Use this for inputs that should never contain HTML
 */
export const sanitizeText = (text: string): string => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

/**
 * Sanitizes email addresses
 * Removes potentially dangerous characters while preserving valid email format
 */
export const sanitizeEmail = (email: string): string => {
  // Remove any HTML tags first
  const textOnly = email.replace(/<[^>]*>/g, '');
  // Allow only valid email characters
  return textOnly.replace(/[^a-zA-Z0-9@.\-+_]/g, '');
};

/**
 * Sanitizes search queries
 * Preserves search functionality while removing dangerous characters
 */
export const sanitizeSearchQuery = (query: string): string => {
  // Remove HTML tags
  const textOnly = query.replace(/<[^>]*>/g, '');
  // Remove potentially dangerous characters but keep search operators
  return textOnly.replace(/[<>"'`]/g, '');
};

/**
 * Sanitizes usernames
 * Allows alphanumeric, underscores, hyphens, and spaces
 */
export const sanitizeUsername = (username: string): string => {
  // Remove HTML tags
  const textOnly = username.replace(/<[^>]*>/g, '');
  // Allow only safe characters for usernames
  return textOnly.replace(/[^a-zA-Z0-9\-_ ]/g, '');
};

/**
 * Sanitizes numeric input
 * Ensures only valid numbers are accepted
 */
export const sanitizeNumber = (value: string): string => {
  return value.replace(/[^0-9.-]/g, '');
};

/**
 * Sanitizes card names and set names.
 *
 * Apostrophes, quotes and backticks are preserved VERBATIM. They are extremely
 * common in Magic card names (Gaea's Cradle, Urza's Saga, Sensei's Divining Top),
 * and this value's destinations already escape correctly for their own context:
 * URLSearchParams percent-encodes it for the wire, and React escapes it in text
 * nodes. HTML-entity-encoding here instead corrupted the query — "Gaea's Cradle"
 * went out as "Gaea&#39;s Cradle" and matched nothing.
 *
 * What is removed is anything that only matters to an HTML sink: tags, stray
 * angle brackets, and control characters. If a caller ever routes this value into
 * innerHTML/dangerouslySetInnerHTML, that call site is the bug — escape at the sink.
 */
export const sanitizeCardName = (name: string): string => {
  const withoutTags = name.replace(/<[^>]*>/g, '').replace(/[<>]/g, '');
  // Drop control characters by code point. A regex range would need literal
  // control bytes in the source; this keeps the file plain ASCII.
  let out = '';
  for (const char of withoutTags) {
    const code = char.codePointAt(0) ?? 0;
    if (code >= 32 && code !== 127) out += char;
  }
  return out;
};

/**
 * Sanitizes multiline text (like feedback/comments)
 * Preserves line breaks but removes dangerous content
 */
export const sanitizeMultilineText = (text: string): string => {
  // First sanitize HTML
  const cleaned = DOMPurify.sanitize(text, {
    ALLOWED_TAGS: ['br'],
    ALLOWED_ATTR: [],
    ALLOW_DATA_ATTR: false,
  });
  // Preserve line breaks
  return cleaned.replace(/\n/g, '<br>');
};

/**
 * Validates and sanitizes URLs
 * Ensures only safe protocols are allowed
 */
export const sanitizeURL = (url: string): string => {
  try {
    const parsed = new URL(url);
    // Only allow http, https, and mailto protocols
    if (!['http:', 'https:', 'mailto:'].includes(parsed.protocol)) {
      return '';
    }
    return parsed.toString();
  } catch {
    return '';
  }
};

/**
 * Sanitizes form data object
 * Applies appropriate sanitization based on field types
 */
export const sanitizeFormData = <T extends Record<string, any>>(
  data: T,
  fieldTypes: Partial<Record<keyof T, 'text' | 'email' | 'html' | 'number' | 'url' | 'multiline'>>
): T => {
  const sanitized = { ...data };
  
  for (const [key, value] of Object.entries(data)) {
    if (typeof value !== 'string') continue;
    
    const fieldType = fieldTypes[key as keyof T] || 'text';
    
    switch (fieldType) {
      case 'email':
        sanitized[key as keyof T] = sanitizeEmail(value) as any;
        break;
      case 'html':
        sanitized[key as keyof T] = sanitizeHTML(value) as any;
        break;
      case 'number':
        sanitized[key as keyof T] = sanitizeNumber(value) as any;
        break;
      case 'url':
        sanitized[key as keyof T] = sanitizeURL(value) as any;
        break;
      case 'multiline':
        sanitized[key as keyof T] = sanitizeMultilineText(value) as any;
        break;
      case 'text':
      default:
        sanitized[key as keyof T] = sanitizeText(value) as any;
        break;
    }
  }
  
  return sanitized;
};

/**
 * Input validation patterns
 */
export const validationPatterns = {
  email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  username: /^[a-zA-Z0-9_-]{3,20}$/,
  cardName: /^[a-zA-Z0-9\s,.:'\-()!?]+$/,
  numeric: /^\d+(\.\d{1,2})?$/,
  alphanumeric: /^[a-zA-Z0-9\s]+$/,
};

/**
 * Validates input against a pattern
 */
export const validateInput = (value: string, pattern: RegExp): boolean => {
  return pattern.test(value);
};

/**
 * Sanitizes and validates input in one step
 */
export const sanitizeAndValidate = (
  value: string,
  sanitizer: (value: string) => string,
  validator?: RegExp
): { value: string; isValid: boolean } => {
  const sanitized = sanitizer(value);
  const isValid = validator ? validateInput(sanitized, validator) : true;
  return { value: sanitized, isValid };
};